import Logger from "../Logger";
import { type UniversalstorageHandler } from "../persistence/UniversalstorageHandler";
import * as pdfjsLib from "pdfjs-dist";
// Worker aus derselben pdfjs-dist-Version wie die API (vermeidet Version-Mismatch 4.10 vs 4.0,
// siehe coverPagePdf.ts/CoverPageFromPdf.ts/floorplanExtraction.ts) - ersetzt die früher hier
// verwendeten, separat gepflegten statischen Kopien unter /pdf/pdf.mjs und /pdf/pdf.worker.mjs,
// die inzwischen hinter der npm-Paketversion zurückgeblieben waren.
import pdfjsWorkerUrl from "pdfjs-dist/build/pdf.worker.mjs?url";
const logger = new Logger();

function ensurePdfjsWorkerSrc(): void {
  if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
    pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorkerUrl;
  }
}

export default class PdfFile {
  url: string | undefined = undefined;
  pageIsLoading = false;
  pageIsRendering = false;

  // the raw pdfFileData
  pdfData: any | undefined;

  // pdf as object
  pdf: any | undefined;

  private loadingTask: ReturnType<typeof pdfjsLib.getDocument> | undefined;
  private renderTask: pdfjsLib.RenderTask | undefined;
  private destroyed = false;

  needstoBeSaved = true;

  async saveToDatabase(id: string): Promise<boolean> {
    if (this.pdfData) {
      try {
        this.needstoBeSaved = false;
        if (this.pdfData) {
          // If pdfData is a binary string (from FileReader.readAsBinaryString),
          // new Blob([string]) encodes it as UTF-8 — bytes ≥ 0x80 become multi-byte
          // sequences, corrupting the PDF when loaded back.
          // Convert to Uint8Array first to preserve raw binary data.
          let blobData: BlobPart;
          if (typeof this.pdfData === "string") {
            blobData = Uint8Array.from(this.pdfData, (c) => c.charCodeAt(0));
          } else {
            blobData = this.pdfData;
          }
          const blob = new Blob([blobData]);
          const res = await ((globalThis as any).universalstorageHandler as UniversalstorageHandler).savePdfFile(
            id,
            blob,
          );

          return res;
        }
      } catch (ex) {
        logger.error(ex);
      }
    }

    return false;
  }

  async loadFromDatabase(id: string): Promise<boolean> {
    try {
      this.needstoBeSaved = false;
      const blob = await ((globalThis as any).universalstorageHandler as UniversalstorageHandler).loadPdfFile(id);
      if (blob) {
        // Use arrayBuffer() instead of text() to avoid UTF-8 re-encoding corrupting binary PDF data
        const arrayBuffer = await blob.arrayBuffer();
        this.pdfData = new Uint8Array(arrayBuffer);
      }
    } catch (ex) {
      logger.error(ex);
    }

    return false;
  }

  async convertPdfToDataUrl(params: {
    pageNumber: number;
    targetHeight: number;
    targetWidth: number;
    rotation: number;
  }): Promise<string> {
    const canvas = document.createElement("canvas") as HTMLCanvasElement;

    //  const canvas = document.getElementById("testCanvas") as HTMLCanvasElement;
    if (canvas && !this.destroyed) {
      if (!(await this.activate()) || this.destroyed) return "";
      const pageSize = await this.getPageSize(params.pageNumber);
      if (pageSize) {
        const rendered = await this.drawPdfFileToCanvas(canvas, params.pageNumber, {
          zoom: 1,
          dx: 0,
          dy: 0,
          targetHeight: params.targetHeight,
          targetWidth: params.targetWidth,
          rotation: params.rotation,
        });

        if (rendered && !this.destroyed) return canvas.toDataURL("image/jpeg");
      }
    }
    return "";
  }

  async activate(): Promise<boolean> {
    if (this.destroyed) return false;

    if (this.pdf) {
      return true;
    }

    if (this.pageIsLoading) {
      return false;
    }

    this.pageIsLoading = true;

    try {
      ensurePdfjsWorkerSrc();

      logger.log("loadPdfFile");

      // Asynchronous download of PDF.
      // pdfjs requires a Uint8Array — binary strings from FileReader.readAsBinaryString
      // and raw blobs loaded via blob.text() are not reliably accepted by newer pdfjs.
      let pdfData: Uint8Array | undefined;
      if (typeof this.pdfData === "string") {
        pdfData = Uint8Array.from(this.pdfData, (c) => c.charCodeAt(0));
      } else if (this.pdfData instanceof Uint8Array) {
        pdfData = this.pdfData;
      } else if (this.pdfData) {
        pdfData = new Uint8Array(this.pdfData);
      }

      // Normalise pdfData to the Uint8Array we actually use — this way callers
      // (e.g. UploadedPdfViewer) can create a blob from exactly the bytes that
      // pdfjs successfully parsed, avoiding binary-string / encoding mismatches.
      if (pdfData) {
        this.pdfData = pdfData;
      }

      // Pass a COPY to pdfjs: the worker thread transfers (detaches) the original
      // ArrayBuffer for zero-copy performance. After the transfer this.pdfData would
      // have byteLength=0 and any later Blob built from it would be empty.
      const pdfDataForPdfjs = pdfData?.slice();

      const loadingTask = this.url ? pdfjsLib.getDocument(this.url) : pdfjsLib.getDocument({ data: pdfDataForPdfjs });
      this.loadingTask = loadingTask;

      const pdf = await loadingTask.promise;
      if (this.destroyed) {
        await loadingTask.destroy();
        return false;
      }
      this.pdf = pdf;
      return true;
    } catch (ex) {
      logger.error(ex);
      return false;
    } finally {
      // Always reset the flag so callers waiting on pageIsLoading are unblocked
      this.pageIsLoading = false;
      this.loadingTask = undefined;
    }
  }

  async destroy(): Promise<void> {
    if (this.destroyed) return;
    this.destroyed = true;

    const renderTask = this.renderTask;
    this.renderTask = undefined;
    renderTask?.cancel();
    if (renderTask) {
      await renderTask.promise.catch(() => undefined);
    }

    const loadingTask = this.loadingTask;
    this.loadingTask = undefined;
    if (loadingTask) {
      await loadingTask.destroy().catch(() => undefined);
    }

    const pdf = this.pdf;
    this.pdf = undefined;
    this.pdfData = undefined;
    this.pageIsLoading = false;
    this.pageIsRendering = false;
    if (pdf) {
      await pdf.destroy().catch(() => undefined);
    }
  }

  async loadFromDatabaseAndActivate(id: string): Promise<boolean> {
    if (this.pdfData == undefined) {
      await this.loadFromDatabase(id);
    }

    if (this.pdfData) {
      await this.activate();
    }

    return this.pdf != undefined;
  }

  async getDefaultRotation(): Promise<number> {
    if (!(await this.activate()) || !this.pdf) return 0;
    return this.pdf.rotation;
  }

  async getPageSize(pageNumber: number): Promise<DOMRect | undefined> {
    try {
      if (!(await this.activate()) || !this.pdf) return undefined;
      const page = await this.pdf.getPage(pageNumber);
      var viewport = page.getViewport({ scale: 1 });

      return new DOMRect(0, 0, viewport.width, viewport.height);
    } catch (ex) {
      logger.error(ex);
    }

    return undefined;
  }

  async drawPdfFileToCanvas(
    canvas: HTMLCanvasElement,
    pageNumber: number,
    transform: {
      zoom: number;
      targetHeight: number;
      targetWidth: number;
      dx: number;
      dy: number;
      rotation: number;
    },
  ): Promise<boolean> {
    if (!(await this.activate()) || this.destroyed || !this.pdf) return false;

    if (this.pageIsRendering) {
      return false;
    }

    this.pageIsRendering = true;

    logger.log(`drawPdfFileToCanvas ${transform.dx} `);

    // Fetch the first page
    var page: any;
    try {
      logger.log(`load Page${pageNumber}`);
      page = await this.pdf.getPage(pageNumber);
      logger.log(`Page loaded ${pageNumber}`, page);
    } catch (ex) {
      if (!this.destroyed) logger.error(ex);
      this.pageIsRendering = false;
      return false;
    }

    if (this.destroyed || !this.pdf) {
      this.pageIsRendering = false;
      return false;
    }

    let viewport = page.getViewport({ scale: 1 });
    const scaleY: number = transform.targetHeight / viewport.height; //or width
    const scaleX: number = transform.targetWidth / viewport.width; //or width

    let scale = scaleX > scaleY ? scaleX : scaleY;

    viewport = page.getViewport({
      scale: scale,
      // offsetX: transform.dx, //  (transform.dx / scale) * transform.zoom,
      // korrekt:      offsetX: (-transform.targetWidth / 2) * transform.zoom, //  (transform.dx / scale) * transform.zoom,
      // offsetY: transform.dy,
      // rotation: transform.rotation,
    });

    const w = viewport.width;
    const h = viewport.height;

    canvas.height = h;
    canvas.width = w;

    canvas.style.height = `${h}px`;
    canvas.style.width = `${w}px`;

    // Prepare canvas using PDF page dimensions
    var context = canvas.getContext("2d");

    const transform2 = [scale, 0, 0, scale, 0, 0];

    // Render PDF page into canvas context
    var renderContext = {
      intent: "print", // no itx
      // transform: transform2,
      canvasContext: context,
      viewport: viewport,
    };

    const renderTask = page.render(renderContext);
    this.renderTask = renderTask;
    try {
      await renderTask.promise;
      logger.log("Page rendered", canvas.width, canvas.height);
    } catch (ex) {
      if (!this.destroyed) logger.error(ex);
      return false;
    } finally {
      if (this.renderTask === renderTask) this.renderTask = undefined;
      this.pageIsRendering = false;
    }

    return !this.destroyed;
  }
}
